import {
  AbstractAuthModuleService,
  AuthToken,
  CORE_SEED_CONFIG_TOKEN,
  UserModel,
  WebAuthnAuthenticateFinishResponse,
  preparePublicKeyCredentialCreationOptions,
  preparePublicKeyCredentialRequestOptions
} from '@aionzdev/modules';
import { computed, DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, filter, iif, map, Observable, of, shareReplay, Subject, switchMap, take, tap, throwError } from 'rxjs';
import { Usuario } from '../models/usuario';
import { mapUsuarioToUserModel } from '../mappers/user.mapper';
import { AuthApiService } from './api/auth-api.service';
import { AuthTokenService } from './auth-token.service';
import { CompanySelectionCookieService } from './company-selection-cookie.service';


@Injectable({ providedIn: 'root' })
export class AuthModuleService extends AbstractAuthModuleService {
  private _service = inject(AuthApiService);
  private _tokenService = inject(AuthTokenService);
  private _companySelectionCookie = inject(CompanySelectionCookieService);
  private _router = inject(Router);
  private _coreConfig = inject(CORE_SEED_CONFIG_TOKEN);
  private readonly window: Window = inject(DOCUMENT).defaultView?.window as Window;

  private readonly _userAuthenticatedCache = signal<Usuario | null>(null);

  /** Reactive representation of the session user for long-lived layout consumers. */
  readonly authenticatedUser = computed<UserModel | null>(() => {
    const user = this._userAuthenticatedCache();
    return user ? mapUsuarioToUserModel(user) : null;
  });

  private readonly _sessionCleared$ = new Subject<void>();
  /**
   * Emite toda vez que a sessão é encerrada (`logout`) ou reiniciada
   * (`authenticate`). Cada serviço `root` que retém estado de sessão assina no
   * próprio construtor e chama seu `resetSession()` — evita ciclo de DI e
   * mantém cada serviço dono do seu reset. Ver
   * docs/specs/limpeza-de-sessao-no-logout.md.
   */
  readonly sessionCleared$ = this._sessionCleared$.asObservable();

  get currentUser(): Usuario | null {
    return this._userAuthenticatedCache();
  }

  public isAuthenticatedChange$ = this.onTokenChange().pipe(
    switchMap(() => this.isAuthenticated()),
    shareReplay(1)
  );

  public readonly userAuthenticatedChange$: Observable<Usuario> = this.isAuthenticatedChange$.pipe(
    filter(isAuthenticated => isAuthenticated),
    switchMap(() => {
      const userCache = this._userAuthenticatedCache();
      return iif(() => !!userCache, of(userCache as Usuario), this.me());
    }),
    shareReplay(1)
  );

  override getAuthToken(): Observable<AuthToken> {
    return this._tokenService.get();
  }

  override isAuthenticated(): Observable<boolean> {
    return this.getAuthToken().pipe(map(authToken => !!authToken.token));
  }

  override userAuthenticated(): Observable<UserModel> {
    return this.userAuthenticatedChange$.pipe(
      map(user => mapUsuarioToUserModel(user)),
      take(1)
    );
  }

  onTokenChange(): Observable<AuthToken | null> {
    return this._tokenService.authTokenChange();
  }

  override authenticate(params: { userName: string; password: string; }): Observable<string> {
    // Login parte de estado limpo — cobre aba reaberta com storage sujo ou
    // login anterior que falhou no meio.
    this.notifySessionCleared();
    return this._service.login(params.userName, params.password)
      .pipe(
        tap(auth => {
          this._userAuthenticatedCache.set(auth);
          this.processToken(auth);
        }),
        map(auth => auth.tokenAplicacao)
      );
  }

  override requestPassword(userName: string): Observable<void> {
    return this._service.requestPassword(userName);
  }

  override resetPassword(params: { newPassword: string; token: string; }): Observable<any> {
    return this._service.getUserByHash(params.token).pipe(
      switchMap(user =>
        this._service.resetPassword(String(user.id), params.token, params.newPassword)
      ),
      catchError(err => {
        if (err.status === 400) {
          // this.openMessageModal({
          //   subtitle: 'Token expirado!',
          //   description:
          //     'Seu token de mudança de senha expirou. Ele é válido por 1 hora. Por favor, solicite um novo.'
          // })
          //   .afterClosed()
          //   .subscribe(() => {
          //     this._router.navigate(['/', 'login']);
          //   });
        } else {
          // this.openMessageModal({
          //   subtitle: 'Ops, algo deu errado!',
          //   description: 'Por favor, verifique as informações inseridas e tente novamente.',
          //   labelConfirm: 'Tentar novamente'
          // });
        }
        return throwError(() => err);
      })
    );
  }

  override register(params: { name: string; userName: string; password: string; phone?: string; email?: string; }): Observable<any> {
    throw new Error('Method not implemented.');
  }

  override isAuthenticatedOrRefresh(): Observable<boolean> {
    return this.getAuthToken().pipe(
      switchMap(token => {
        if (token?.isValid()) {
          return of(true);
        }
        if (token?.isRefreshTokenValid()) {
          return this.refreshToken().pipe(
            switchMap(() => this.isAuthenticated()),
            catchError(() => of(false))
          );
        }
        return of(false);
      })
    );
  }

  override refreshToken(): Observable<string> {
    return this.getAuthToken().pipe(
      switchMap(token => this._service.refreshToken(token.refreshToken)),
      tap(usuario => {
        this._userAuthenticatedCache.set(usuario);
        this.processToken(usuario);
      }),
      map(usuario => usuario.tokenAplicacao),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  override startRegistration(): Observable<PublicKeyCredentialCreationOptions> {
    return this._service.startWebAuthnRegistration(this._coreConfig.tenantName, this.rpID).pipe(
      map(response => preparePublicKeyCredentialCreationOptions(response.options))
    );
  }

  override finishRegistration(params: {
    response: PublicKeyCredential;
    credentialName?: string;
  }): Observable<any> {
    return this._service.finishWebAuthnRegistration({
      response: params.response,
      credentialName: params.credentialName,
      origin: this.origin,
      rpID: this.rpID
    });
  }

  override startAuthentication(): Observable<PublicKeyCredentialRequestOptions> {
    return this._service.startWebAuthnAuthentication(this.rpID).pipe(
      map(response => preparePublicKeyCredentialRequestOptions(response.options))
    );
  }

  override finishAuthentication(response: PublicKeyCredential): Observable<any> {
    return this._service
      .finishWebAuthnAuthentication({
        response,
        origin: this.origin,
        rpID: this.rpID
      })
      .pipe(
        switchMap((authResponse: WebAuthnAuthenticateFinishResponse) => {
          const usuario = authResponse.user as unknown as Usuario;
          this._userAuthenticatedCache.set(usuario);
          return this.processToken(usuario);
        }),
        map(usuario => usuario.tokenAplicacao)
      );
  }

  public me(): Observable<Usuario> {
    return this._service.getMe().pipe(
      tap(user => this._userAuthenticatedCache.set(user)),
      catchError(err => {
        if (err.status === 404 || err.status === 401) {
          this.logout(false);
          return of({} as Usuario);
        }
        return throwError(() => err);
      })
    );
  }

  override logout(disableLoginAutomatic?: boolean): void {
    if (this.window.navigator.credentials?.preventSilentAccess && disableLoginAutomatic) {
      this.window.navigator.credentials.preventSilentAccess();
    }

    this.notifySessionCleared();
    this._router.navigate(['/', 'login']);
  }

  /**
   * Limpa o que é responsabilidade direta deste serviço: cache do usuário,
   * cookies de token e de empresa escolhida. `_userAuthenticatedCache` nulo é o
   * que faz `userAuthenticatedChange$` voltar a chamar `me()` no próximo login
   * (senão o curto-circuito serve o usuário anterior).
   */
  private clearOwnSession(): void {
    this._userAuthenticatedCache.set(null);
    this._tokenService.clear();
    this._companySelectionCookie.clear();
  }

  private notifySessionCleared(): void {
    this.clearOwnSession();
    this._sessionCleared$.next();
  }

  private processToken(user: Usuario) {
    return this._tokenService.set(
      new AuthToken({
        token: user.tokenAplicacao,
        refreshToken: user.refreshToken
      })
    )
      .pipe(map(() => user));
  }

}
